import { z } from 'zod';
import he from 'he';
import { formatUSPhoneForDisplay } from './phone-utils.js';
/**
 * Session-end description composition: which elements a handoff action's
 * output includes (stored per action in `parameters.conversationDescriptionOptions`)
 * and how each target's platform budget is enforced.
 */
export const ConversationDescriptionOptionsSchema = z.object({
    includeContactDetails: z.boolean().optional(),
    includeTranscript: z.boolean().optional(),
    includeRecordingUrl: z.boolean().optional(),
});
export const CONVERSATION_DESCRIPTION_OPTIONS_PARAM_KEY = 'conversationDescriptionOptions';
/**
 * Merge an action's stored (partial) options over the target's defaults.
 * Malformed or absent values resolve to the defaults; the summary is always on.
 * `onInvalid` fires only when a value is present but malformed (not when
 * absent) so a caller with a logger can surface silent drift; shared-types
 * stays logger-free.
 */
export function resolveConversationDescriptionOptions(actionParameters, defaults, onInvalid) {
    const stored = actionParameters?.[CONVERSATION_DESCRIPTION_OPTIONS_PARAM_KEY];
    const parsed = ConversationDescriptionOptionsSchema.safeParse(stored);
    if (!parsed.success) {
        if (stored !== undefined && stored !== null)
            onInvalid?.();
        return { ...defaults, includeSummary: true };
    }
    return { ...defaults, ...parsed.data, includeSummary: true };
}
const DEFAULT_LINK_LABEL = 'Recording';
const DEFAULT_TRANSCRIPT_HEADER = '--- Transcript ---';
export const DEFAULT_TRUNCATION_MARKER = '[...]';
/** Escape for HTML output; guards non-strings that `he.escape` would throw on. */
export function escapeHtml(text) {
    return text ? he.escape(text) : '';
}
/**
 * Cap a push title/subject to a platform limit, appending an ellipsis when
 * cut. Applied where the title is produced (it's a separate form field from
 * the description body). No limit → returned trimmed unchanged.
 */
export function capPushTitle(title, maxTitleChars) {
    const value = (title ?? '').trim();
    if (maxTitleChars === undefined || value.length <= maxTitleChars)
        return value;
    return value.slice(0, Math.max(0, maxTitleChars - 1)).trimEnd() + '…';
}
export function formatContactLine(contactDetails) {
    const parts = [
        contactDetails?.name,
        contactDetails?.phoneNumber
            ? formatUSPhoneForDisplay(contactDetails.phoneNumber)
            : null,
        contactDetails?.email,
    ].filter((p) => !!p && p.trim() !== '');
    return parts.length > 0 ? parts.join(' · ') : null;
}
function countWords(text) {
    const trimmed = text.trim();
    return trimmed === '' ? 0 : trimmed.split(/\s+/).length;
}
/**
 * Largest prefix of `raw` whose transformed length fits `budget` — measured on
 * the output so escaping (`&` → `&amp;`) never splits an entity. `@internal`,
 * exported only for unit tests.
 */
export function fitTransformedPrefix(raw, budget, transform) {
    let n = Math.min(raw.length, budget);
    while (n > 0) {
        const out = transform(raw.slice(0, n));
        if (out.length <= budget)
            return out;
        n = Math.min(n - 1, Math.floor((n * budget) / out.length));
    }
    return null;
}
/**
 * The single shed-order loop: the transcript absorbs all truncation first,
 * then contact details are dropped, then the link; the summary is cut only
 * as a last resort.
 */
function enforceBudget(hasTranscript, r) {
    if (hasTranscript) {
        const withTranscript = r.fitTranscript();
        if (withTranscript !== null)
            return withTranscript;
    }
    const shedOrder = [
        { summary: true, link: true, contact: true, transcript: false },
        { summary: true, link: true, contact: false, transcript: false },
        { summary: true, link: false, contact: false, transcript: false },
    ];
    for (const flags of shedOrder) {
        const rendered = r.render(flags);
        if (r.fits(rendered))
            return rendered;
    }
    return r.trimSummary();
}
/**
 * Build the description a push integration sends at session finality:
 * enabled sections in priority order (summary → link → contact → transcript),
 * then the platform budget enforced via `enforceBudget`. Callers must not
 * re-truncate `text`.
 */
export function buildPushDescription(content, options, profile) {
    const marker = profile.truncationMarker ?? DEFAULT_TRUNCATION_MARKER;
    const summary = (() => {
        if (!options.includeSummary || !content.summary)
            return null;
        const value = profile.normalizeSummaryWhitespace
            ? content.summary.replace(/\s+/g, ' ').trim()
            : content.summary;
        return value === '' ? null : value;
    })();
    const linkUrl = options.includeRecordingUrl && content.linkUrl ? content.linkUrl : null;
    const contactLine = options.includeContactDetails
        ? formatContactLine(content.contactDetails)
        : null;
    const transcript = options.includeTranscript && content.transcript?.trim()
        ? content.transcript
        : null;
    return profile.format === 'html'
        ? buildHtml(summary, linkUrl, contactLine, transcript, profile, marker)
        : buildText(summary, linkUrl, contactLine, transcript, profile, marker);
}
// ---------------------------------------------------------------------------
// Text mode
// ---------------------------------------------------------------------------
function buildText(summary, linkUrl, contactLine, transcript, profile, marker) {
    const linkLabel = profile.linkLabel ?? DEFAULT_LINK_LABEL;
    const header = profile.transcriptHeader ?? DEFAULT_TRANSCRIPT_HEADER;
    const render = (flags, transcriptText) => {
        const parts = [];
        if (flags.summary && summary)
            parts.push(summary);
        if (flags.link && linkUrl)
            parts.push(`${linkLabel}: ${linkUrl}`);
        if (flags.contact && contactLine)
            parts.push(`Contact: ${contactLine}`);
        if (flags.transcript && transcript !== null) {
            parts.push(`${header}\n${transcriptText ?? transcript}`);
        }
        return parts.join('\n\n');
    };
    const ALL = {
        summary: true,
        link: true,
        contact: true,
        transcript: true,
    };
    const full = render(ALL);
    const byChars = (maxChars) => ({
        render,
        fits: s => s.length <= maxChars,
        fitTranscript: () => {
            if (transcript === null)
                return null;
            const fixed = render({ ...ALL, transcript: false });
            const separator = (fixed ? '\n\n' : '') + `${header}\n`;
            const available = maxChars - fixed.length - separator.length - marker.length;
            if (available <= 0)
                return null;
            return `${fixed}${separator}${transcript.substring(0, available)}${marker}`;
        },
        trimSummary: () => {
            const summaryOnly = render({
                summary: true,
                link: false,
                contact: false,
                transcript: false,
            });
            if (summaryOnly.length <= maxChars)
                return summaryOnly;
            const available = maxChars - marker.length;
            if (available <= 0)
                return summaryOnly.substring(0, maxChars);
            return `${summaryOnly.substring(0, available)}${marker}`;
        },
    });
    const byWords = (maxWords) => ({
        render,
        fits: s => countWords(s) <= maxWords,
        fitTranscript: () => {
            if (transcript === null)
                return null;
            // An empty transcript still renders its header — count those words too.
            const fixedWords = countWords(render(ALL, ''));
            const available = maxWords - fixedWords;
            if (available <= 0)
                return null;
            const words = transcript.trim().split(/\s+/);
            const kept = words.length > available
                ? words.slice(0, available).join(' ') + marker
                : transcript;
            return render(ALL, kept);
        },
        trimSummary: () => {
            const summaryOnly = render({
                summary: true,
                link: false,
                contact: false,
                transcript: false,
            });
            if (countWords(summaryOnly) <= maxWords)
                return summaryOnly;
            const words = summaryOnly.trim().split(/\s+/);
            return words.slice(0, maxWords).join(' ') + marker;
        },
    });
    if (profile.maxChars !== undefined && full.length > profile.maxChars) {
        return {
            text: enforceBudget(transcript !== null, byChars(profile.maxChars)),
            truncated: true,
            untruncated: full,
        };
    }
    if (profile.maxWords !== undefined && countWords(full) > profile.maxWords) {
        return {
            text: enforceBudget(transcript !== null, byWords(profile.maxWords)),
            truncated: true,
            untruncated: full,
        };
    }
    return { text: full, truncated: false, untruncated: full };
}
// ---------------------------------------------------------------------------
// HTML mode — budgets measured on the escaped output so markup never breaks
// ---------------------------------------------------------------------------
function buildHtml(summary, linkUrl, contactLine, transcript, profile, marker) {
    const anchorText = profile.linkLabel ?? DEFAULT_LINK_LABEL;
    const escapeWithBreaks = (raw) => escapeHtml(raw).replace(/\n/g, '<br>');
    const summarySection = summary !== null ? `<p>${escapeWithBreaks(summary)}</p>` : '';
    const urlSection = linkUrl
        ? `<p><a href="${escapeHtml(linkUrl)}">${escapeHtml(anchorText)}</a></p>`
        : '';
    const contactSection = contactLine
        ? `<p><strong>Contact:</strong> ${escapeHtml(contactLine)}</p>`
        : '';
    const transcriptOpen = '<p><strong>Transcript:</strong></p><pre>';
    const transcriptClose = '</pre>';
    const render = (flags, transcriptHtml) => (flags.summary ? summarySection : '') +
        (flags.link ? urlSection : '') +
        (flags.contact ? contactSection : '') +
        (flags.transcript && transcript !== null
            ? `${transcriptOpen}${transcriptHtml ?? escapeHtml(transcript)}${transcriptClose}`
            : '');
    const full = render({
        summary: true,
        link: true,
        contact: true,
        transcript: true,
    });
    const maxChars = profile.maxChars;
    if (maxChars === undefined || full.length <= maxChars) {
        return { text: full, truncated: false, untruncated: full };
    }
    const renderer = {
        render,
        fits: s => s.length <= maxChars,
        fitTranscript: () => {
            if (transcript === null)
                return null;
            const fixed = render({
                summary: true,
                link: true,
                contact: true,
                transcript: false,
            });
            const available = maxChars -
                fixed.length -
                transcriptOpen.length -
                transcriptClose.length -
                marker.length;
            if (available <= 0)
                return null;
            const escaped = fitTransformedPrefix(transcript, available, escapeHtml);
            if (escaped === null)
                return null;
            return `${fixed}${transcriptOpen}${escaped}${marker}${transcriptClose}`;
        },
        trimSummary: () => {
            const summaryOnly = render({
                summary: true,
                link: false,
                contact: false,
                transcript: false,
            });
            if (summaryOnly.length <= maxChars || summary === null) {
                return summaryOnly.substring(0, Math.max(0, maxChars));
            }
            const available = maxChars - '<p></p>'.length - marker.length;
            const escaped = available > 0
                ? fitTransformedPrefix(summary, available, escapeWithBreaks)
                : null;
            // Only reached below ~12 chars (under any real platform limit), where a
            // raw cut may split a tag/entity.
            if (escaped === null)
                return summaryOnly.substring(0, maxChars);
            return `<p>${escaped}${marker}</p>`;
        },
    };
    return {
        text: enforceBudget(transcript !== null, renderer),
        truncated: true,
        untruncated: full,
    };
}
//# sourceMappingURL=push-description.js.map